import { format } from 'date-fns';
import { isLiveBooking } from '@atink/domain';
import { depositSentByLabel } from '@/domain/depositStatus';
import type { Artist, Booking, CustomerRecord, Deposit, StudioId } from '@/domain/types';

/**
 * D02-07 / D05-19 (Anas, 6 Oct; ClickUp 14ym9bvb3xp): an overbooked this-week
 * slot lost to another client. The server releases the losing hold in the
 * winner's pay (deposit `releasedReason: 'slot-taken'`), and the Agent is
 * alerted to call the client back and find a new date. Derived from that server
 * state alone, so it survives a reload and needs no staff browser at the pay.
 *
 * The alert clears once the client has a live booking made after the release,
 * or a newer link has been sent; it ages out after 14 days.
 */

export const SLOT_TAKEN_ALERT_DAYS = 14;
const DAY_MS = 86_400_000;

export interface SlotTakenAlert {
  depositId: string;
  customerId: string;
  studioId: StudioId;
  /** When the hold was released (the winner's pay). */
  releasedAt: string;
  /** "Asha Indi Mon 11 May 2:00pm was paid by another client first…" */
  detail: string;
}

export function buildSlotTakenAlerts(args: {
  nowIso: string;
  deposits: Record<string, Deposit>;
  bookings: Record<string, Booking>;
  customers: Record<string, CustomerRecord>;
  artists: Record<string, Artist>;
  /** Studio scope; empty = every studio. */
  studioIds?: StudioId[];
}): SlotTakenAlert[] {
  const nowMs = Date.parse(args.nowIso);
  const scope = args.studioIds && args.studioIds.length ? new Set(args.studioIds) : null;
  const deposits = Object.values(args.deposits);
  const out: SlotTakenAlert[] = [];
  for (const d of deposits) {
    if (d.releasedReason !== 'slot-taken' || d.status !== 'expired' || !d.releasedAt) continue;
    if (scope && !scope.has(d.studioId)) continue;
    const releasedMs = Date.parse(d.releasedAt);
    if (!Number.isFinite(releasedMs) || nowMs - releasedMs > SLOT_TAKEN_ALERT_DAYS * DAY_MS) continue;
    const c = args.customers[d.customerId];
    if (!c || c.doNotContact) continue;
    // Worked already: booked again, or a fresh link sent since the release.
    const bookedSince = Object.values(args.bookings).some(
      (b) => b.customerId === c.id && isLiveBooking(b) && Date.parse(b.createdAt) > releasedMs,
    );
    if (bookedSince) continue;
    const newerLink = deposits.some(
      (o) => o.id !== d.id && o.customerId === c.id && Date.parse(o.createdAt) > releasedMs,
    );
    if (newerLink) continue;

    const held = d.heldBookingId ? args.bookings[d.heldBookingId] : undefined;
    const startIso = held?.start ?? d.discussedSlot?.startIso;
    const artistId = held?.artistId ?? d.artistId ?? d.discussedSlot?.artistId;
    const artist = artistId ? args.artists[artistId] : undefined;
    const who = artist?.displayName ?? artist?.firstName ?? 'The artist';
    const when = startIso ? ` ${format(new Date(startIso), 'EEE d MMM h:mmaaa')}` : '';
    out.push({
      depositId: d.id,
      customerId: c.id,
      studioId: d.studioId,
      releasedAt: d.releasedAt,
      detail: `${who}${when} was paid by another client first. Call back to pick a new date. Link sent by ${depositSentByLabel(d)}.`,
    });
  }
  return out.sort((a, b) => b.releasedAt.localeCompare(a.releasedAt));
}
